import DataGridComponent from "components/DataGridComponent/DataGridComponent"
import { DeleteDialog } from "components/DeleteDialog/DeleteDialog"
import { ChapterType, DataNode, Norm, TableCell, TextBlock } from "graphql/types"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { useEffect, useState } from "react"
import { getChapterName } from "store/reportChaptersTemplate"
import { getNormName } from "utils/getNormName"
import { v4 } from "uuid"
import { AddTextBlockDialog } from "./AddTextBlockDialog"

const AdminTextBlocks = () => {
  const { textBlocks, examinationConfigs, norms } = useStoreState((state) => state)
  const { updateTextBlock, deleteTextBlock, fetchTextBlocks, fetchExaminationConfigs, fetchNorms } =
    useStoreActions((actions) => actions)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [formState, setFormState] = useState<TextBlock>({
    id: "NEW",
    block: [],
    chapter: ChapterType.EUT,
    examinations: [],
    norms: [],
    isTestRelevant: false,
    name: "",
  })
  const [openDelete, setOpenDelete] = useState(false)
  const [toDelete, setToDelete] = useState<string[]>([])

  useEffect(() => {
    const loadExaminationConfigs = async () => {
      try {
        await fetchExaminationConfigs({ fresh: false })
      } catch (err) {
        console.error("Error loading examination configs:", err)
      }
    }

    const loadNorms = async () => {
      try {
        await fetchNorms({ fresh: false })
      } catch (err) {
        console.error("Error loading norms:", err)
      }
    }

    const loadTextBlocks = async () => {
      try {
        await fetchTextBlocks({ fresh: false })
      } catch (err) {
        console.error("Error loading text blocks:", err)
      }
    }
    loadTextBlocks()
    loadExaminationConfigs()
    loadNorms()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleEdit = (id: string) => {
    if (id === "NEW") {
      setFormState({
        id: "NEW",
        block: [],
        chapter: ChapterType.EUT,
        examinations: [],
        norms: [],
        isTestRelevant: false,
        name: "",
      })
    } else {
      const textBlock = textBlocks.find((tb) => tb.id === id)
      if (textBlock) {
        setFormState(textBlock)
      }
    }
    setDialogOpen(true)
  }

  const handleDelete = (id: string[]) => {
    setOpenDelete(true)
    setToDelete(id)
  }

  const handleDeleteSelected = () => {
    deleteTextBlock(toDelete)
    setToDelete([])
    setOpenDelete(false)
  }

  const handleDialogClose = () => {
    setDialogOpen(false)
  }

  const handleFormSubmit = (arg: TextBlock) => {
    updateTextBlock(arg)
    setDialogOpen(false)
  }

  const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

  const getCopyBaseName = (name: string) => {
    const trimmed = name.trim()
    const withoutSuffix = trimmed.replace(/\s*\(\d+\)$/u, "").trim()
    if (withoutSuffix.length > 0) {
      return withoutSuffix
    }
    if (trimmed.length > 0) {
      return trimmed
    }
    return "Textblock"
  }

  const getNextCopyName = (name: string) => {
    const baseName = getCopyBaseName(name)
    const pattern = new RegExp(`^${escapeRegExp(baseName)}(?: \\((\\d+)\\))?$`)
    let highestNumber = 0

    textBlocks.forEach((block) => {
      const match = block.name.match(pattern)
      if (match) {
        const suffix = match[1] ? parseInt(match[1], 10) : 0
        if (!Number.isNaN(suffix) && suffix > highestNumber) {
          highestNumber = suffix
        }
      }
    })

    const nextNumber = highestNumber + 1
    return `${baseName} (${nextNumber})`
  }

  const cloneTableCell = (cell: TableCell): TableCell => {
    const { __typename, ...rest } = cell
    return { ...rest }
  }

  const cloneDataNode = (node: DataNode): DataNode => {
    const { __typename, tableBody, tableHeader, image, imageCaption, imageNumber, ...rest } = node

    return {
      ...rest,
      id: v4(),
      image: [...(image ?? [])],
      imageCaption: [...(imageCaption ?? [])],
      imageNumber: [...(imageNumber ?? [])],
      tableHeader: (tableHeader ?? []).map((headerCell) => cloneTableCell(headerCell)),
      tableBody: (tableBody ?? []).map((row) =>
        (row ?? []).map((cell) => cloneTableCell(cell))
      ),
    }
  }

  const handleCopy = (id: string) => {
    const textBlock = textBlocks.find((tb) => tb.id === id)
    if (!textBlock) {
      return
    }

    const { __typename, block, examinations, norms, ...rest } = textBlock
    const copiedTextBlock: TextBlock = {
      ...rest,
      id: "NEW",
      name: getNextCopyName(textBlock.name),
      block: (block ?? []).map((node) => cloneDataNode(node)),
      examinations: [...(examinations ?? [])],
      norms: [...(norms ?? [])],
    }

    updateTextBlock(copiedTextBlock)
  }

  const columns = [
    {
      field: "name",
      headerName: "Name",
      flex: 200,
    },
    {
      field: "chapter",
      headerName: "Chapter",
      flex: 200,
      valueGetter: (row: ChapterType) => getChapterName(row),
    },
    {
      field: "examinations",
      headerName: "Examination",
      flex: 200,
      valueGetter: (params: string[]) =>
        params.map((id) => examinationConfigs.find((e) => e.id === id)?.name).join(", "),
    },
    {
      field: "norms",
      headerName: "Norms",
      flex: 200,
      valueGetter: (params: string[]) => {
        const norm = params
          .map((id) => norms.find((n) => n.id === id))
          .filter((n) => n !== undefined) as Norm[]
        return norm.map((n) => getNormName(n)).join(", ")
      },
    },
    {
      field: "isTestRelevant",
      headerName: "Testrelevant",
      flex: 200,
      valueGetter: (params: boolean) => (params ? "Ja" : "Nein"),
    },
  ]

  return (
    <>
      <DataGridComponent
        columns={columns}
        rows={textBlocks}
        getRowId={(row) => row.id}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onAdd={() => handleEdit("NEW")}
        onCopy={handleCopy}
      />

      <AddTextBlockDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        formState={formState}
        onSubmit={handleFormSubmit}
      />

      <DeleteDialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        onDelete={handleDeleteSelected}
      />
    </>
  )
}

export default AdminTextBlocks
